import { orgWallClock } from '@/lib/utils/custom-date/custom-date';
import { NOC_ASSET_BASE, NocLetterContent } from '../../utils/noc-pdf';

const stamp = `${NOC_ASSET_BASE}/selise_stamp.png`;

/**
 * On-screen A4 preview of the NOC letter, laid out as a printed letter (HR Module Prototype, Component
 * Map › NOC certificate preview): the organization's letterhead, the body, and a signature block of the
 * approver with the day they approved it, the contact person under it for verification. The downloaded
 * PDF follows the same layout.
 */
export const NocLetterPreview = ({ letter }: { letter: NocLetterContent }) => {
  const { letterhead } = letter;

  return (
    <div className="mx-auto w-full min-w-0 max-w-[794px] light rounded-md border bg-card p-5 text-sm leading-relaxed text-foreground shadow-sm sm:p-12 sm:text-[15px]">
      <img src={letterhead.logoUrl} alt={letterhead.companyName} className="mb-6 h-12 w-auto" />

      <div className="mb-8 flex flex-col gap-4 sm:mb-12 sm:flex-row sm:justify-between sm:gap-6">
        <div className="flex flex-col gap-1">
          <span>{letter.documentNumber}</span>
          <span>{letter.letterDate}</span>
        </div>
        <div className="flex flex-col text-[11px] leading-snug">
          <span className="font-semibold">{letterhead.companyName}</span>
          {letterhead.addressLines.map((line) => (
            <span key={line}>{line}</span>
          ))}
          <div className="mt-2 grid grid-cols-[auto_auto] justify-start gap-x-3">
            {letterhead.contactLines.map(([label, value]) => (
              <div key={label} className="contents">
                <span className="font-semibold text-muted-foreground">{label}</span>
                <span>{value}</span>
              </div>
            ))}
          </div>
          <div className="mt-2 flex flex-col">
            <a
              href={letterhead.website.startsWith('http') ? letterhead.website : `https://${letterhead.website}`}
              target="_blank"
              rel="noreferrer"
            >
              {letterhead.website}
            </a>
            <a href={`mailto:${letterhead.email}`}>{letterhead.email}</a>
          </div>
        </div>
      </div>

      <h2 className="mb-9 text-center text-lg font-bold uppercase">To whom it may concern</h2>
      <div className="mb-16 whitespace-pre-line break-words text-left sm:text-justify">
        {letter.body}
      </div>

      <p className="mb-4">Sincerely,</p>
      <div className="mb-2 flex justify-end sm:mr-14">
        <img src={stamp} alt="" className="h-28 w-28 object-contain" />
      </div>
      <div className="flex flex-col gap-1">
        <div className="mb-2 w-44 border-t border-foreground" />
        <span className="font-bold">{letter.approverName}</span>
        {letter.approverDesignation && <span>{letter.approverDesignation}</span>}
        <span>{letterhead.companyName}</span>
        <span>Approved on {letter.approvedOn}</span>
      </div>

      <div className="mt-6 flex flex-col gap-0.5 text-[13px]">
        <span>
          Contact: {[letter.pocName, letter.pocDesignation].filter(Boolean).join(', ')}
        </span>
        <span>
          <b>Email : </b>
          {letter.pocEmail}
        </span>
        <span>
          <b>Mobile : </b>
          {letter.pocMobile}
        </span>
      </div>

      <div className="mt-14 text-center text-xs text-muted-foreground">
        <span>
          © {letterhead.companyName} {orgWallClock(new Date()).year}
        </span>
        <br />
        <span>
          This document is digitally signed with SELISE Signature and should be treated as an
          original.
        </span>
      </div>
    </div>
  );
};
